import {
    IEventAnalyticGroupedByBatchId,
    IFetchEventAnalyticGroupedByBatchIdRequestParams,
    IFetchEventAnalyticGroupedBySiteRequestParams,
    IEventAnalyticGroupedBySite,
    IEventAnalyticEnergyUsageParams,
    IFetchEarliestEventInBatchParams,
    IFetchEventAnalyticRequestParams,
} from './interface';
import { IPagination } from '../IPagination';
import { API } from '../api';
import { format, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, subQuarters, startOfYear, subYears, endOfYear } from 'date-fns';
import { DATE_FORMAT_CSV } from '../commonConst';
import { exportCSVData } from '../common/exportCSVData';
import { EventAnalyticsDataRangeFilterValues, EventAnalyticsDurationFilterValues } from '../../components/table/columnFormatHelpers';
import { isNumber } from 'lodash';
import { EnergyUsageTimeseriesDto } from '../energy/interface';
import { IEvent } from '../event/interface';

export const exportEventAnalytic = async (params: IFetchEventAnalyticRequestParams, signal?: AbortSignal | null) => {
    let { batchId, companyId, filter, programNames, sorter } = params;

    const { start, end } = getDateRangeFromPreset(filter?.date_range_preset);
    const { min, max } = getDurationFromPreset(filter?.event_duration_preset);

    if (sorter && (!sorter.field || !sorter.order)) {
        sorter = {};
    }

    const hasNomination = filter?.nomination_preset === 'more_than_0';

    const queryString = new URLSearchParams({
        format: 'csv',
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(start && { start: start.toISOString() }),
        ...(end && { end: end.toISOString() }),
        ...(isNumber(min) && { durationMin: min.toString() }),
        ...(max && { durationMax: max.toString() }),
        ...(batchId && { batchId }),
        ...(companyId && { companyId: companyId.toString() }),
        ...(programNames && { programNames }),
        ...(hasNomination && { hasNomination: 'true' }),
        ...(sorter?.field && { sortField: sorter.field, sortOrder: sorter.order }),
    }).toString();

    const path = `/event-analytic?${queryString}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'text/csv',
        },
    });

    if (response.ok) {
        const respBody: string = await response.text();
        const fileNamePrefix = batchId ? 'event-analytics' : 'event-analytics-grouped-by-meter';
        const fileName = `${fileNamePrefix}--${format(new Date(), DATE_FORMAT_CSV)}.csv`;
        exportCSVData(respBody, fileName);
    } else {
        const respBody: any = await response.json();
        throw new Error(respBody?.error?.message || `Cannot export data!`);
    }
};

export const fetchEventAnalyticGroupedByBatch = async (
    params: IFetchEventAnalyticGroupedByBatchIdRequestParams,
    signal: AbortSignal | null = null
): Promise<IPagination<IEventAnalyticGroupedByBatchId>> => {
    const queryString = buildQueryStringForEventAnalyticGroupedByBatch(params);

    const path = `/event-analytic/group/event-batch-id?${queryString}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();
    if (response.ok) {
        return respBody;
    }
    throw new Error(response.statusText);
};

export const exportEventAnalyticGroupedByBatch = async (params: IFetchEventAnalyticGroupedByBatchIdRequestParams, signal?: AbortSignal | null) => {
    params.format = 'csv';

    const queryString = `${buildQueryStringForEventAnalyticGroupedByBatch(params)}&tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`;

    const path = `/event-analytic/group/event-batch-id?${queryString}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'text/csv',
        },
    });

    if (response.ok) {
        const respBody: string = await response.text();
        const fileName = `event-analytics-grouped-by-batch--${format(new Date(), DATE_FORMAT_CSV)}.csv`;
        exportCSVData(respBody, fileName);
    } else {
        const respBody: any = await response.json();
        throw new Error(respBody?.error?.message || `Cannot export data!`);
    }
};

function buildQueryStringForEventAnalyticGroupedByBatch(params: IFetchEventAnalyticGroupedByBatchIdRequestParams): string {
    let { pagination, sorter, companyId, programNames, format, filter } = params;

    let limit;
    let offset;

    if (pagination) {
        limit = pagination.pageSize ?? 10;
        offset = ((pagination.current ?? 1) - 1) * limit;
    }

    if (sorter && (!sorter.field || !sorter.order)) {
        sorter = {};
    }

    const { start, end } = getDateRangeFromPreset(filter?.date_range_preset);
    const { min, max } = getDurationFromPreset(filter?.event_duration_preset);

    const hasNomination = filter?.nomination_preset === 'more_than_0';

    return new URLSearchParams({
        ...(start && { start: start.toISOString() }),
        ...(end && { end: end.toISOString() }),
        ...(isNumber(min) && { durationMin: min }),
        ...(max && { durationMax: max }),
        ...(companyId && { companyId: companyId.toString() }),
        ...(programNames && { programNames }),
        ...(sorter?.field && { sortField: sorter.field, sortOrder: sorter.order }),
        ...(offset && { offset: offset.toString() }),
        ...(limit && { limit: limit.toString() }),
        ...(hasNomination && { hasNomination: true }),
        ...(format && { format }),
    }).toString();
}

function getDateRangeFromPreset(preset?: EventAnalyticsDataRangeFilterValues): { start: Date | null; end: Date | null } {
    const today = new Date();

    switch (preset) {
        case 'current_month':
            return {
                start: startOfMonth(today),
                end: endOfMonth(today),
            };

        case 'current_quarter':
            return {
                start: startOfQuarter(today),
                end: endOfQuarter(today),
            };

        case 'previous_quarter': {
            const previousQuarterDate = subQuarters(today, 1);
            return {
                start: startOfQuarter(previousQuarterDate),
                end: endOfQuarter(previousQuarterDate),
            };
        }

        case 'year_to_date':
            return {
                start: startOfYear(today),
                end: today,
            };

        case 'prior_year': {
            const priorYearDate = subYears(today, 1);
            return {
                start: startOfYear(priorYearDate),
                end: endOfYear(priorYearDate),
            };
        }

        case 'all':
        default:
            return {
                start: null,
                end: null,
            };
    }
}

export function getDurationFromPreset(preset: EventAnalyticsDurationFilterValues): { min: number | null; max: number | null } {
    const MAX_EXPECTED_EVENT_DURATION_MINUTES = 24 * 60;

    switch (preset) {
        case 'less_than_1':
            return { min: 0, max: 59 }; // 0 to <60 minutes

        case '1_to_3':
            return { min: 60, max: 180 }; // 1 hour to 3 hours included

        case 'more_than_3':
            return { min: 181, max: MAX_EXPECTED_EVENT_DURATION_MINUTES }; // 3 hours+

        default:
            return { min: null, max: null }; // fallback: everything
    }
}

export const fetchEventAnalyticGroupedBySite = async (
    params: IFetchEventAnalyticGroupedBySiteRequestParams,
    signal: AbortSignal | null = null
): Promise<IPagination<IEventAnalyticGroupedBySite>> => {
    const queryString = buildQueryStringForEventAnalyticGroupedBySite(params);

    const path = `/event-analytic/group/site?${queryString}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();
    if (response.ok) {
        return respBody;
    }
    throw new Error(response.statusText);
};

function buildQueryStringForEventAnalyticGroupedBySite(params: IFetchEventAnalyticGroupedBySiteRequestParams) {
    let { batchId, pagination, companyId, format, sorter } = params;

    let limit;
    let offset;

    if (pagination) {
        limit = pagination.pageSize ?? 10;
        offset = ((pagination.current ?? 1) - 1) * limit;
    }

    if (sorter && (!sorter.field || !sorter.order)) {
        sorter = {};
    }

    return new URLSearchParams({
        ...(batchId && { batchId }),
        ...(companyId && { companyId: companyId.toString() }),
        ...(sorter?.field && { sortField: sorter.field, sortOrder: sorter.order }),
        ...(offset && { offset: offset.toString() }),
        ...(limit && { limit: limit.toString() }),
        ...(format && { format }),
    }).toString();
}

export const exportEventAnalyticGroupedBySite = async (params: IFetchEventAnalyticGroupedBySiteRequestParams, signal?: AbortSignal | null) => {
    params.format = 'csv';

    const queryString = `${buildQueryStringForEventAnalyticGroupedBySite(params)}&tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`;

    const path = `/event-analytic/group/site?${queryString}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'text/csv',
        },
    });

    if (response.ok) {
        const respBody: string = await response.text();
        const fileName = `event-analytics-grouped-by-site--${format(new Date(), DATE_FORMAT_CSV)}.csv`;
        exportCSVData(respBody, fileName);
    } else {
        const respBody: any = await response.json();
        throw new Error(respBody?.error?.message || `Cannot export data!`);
    }
};

export const getEnergyUsageDuringEvent = async (
    params: IEventAnalyticEnergyUsageParams,
    signal?: AbortSignal | null
): Promise<EnergyUsageTimeseriesDto[]> => {
    const { batchId, companyId, siteId, include, aggregate, san } = params;
    const queryString = new URLSearchParams({
        ...(companyId && { companyId: companyId.toString() }),
        ...(siteId && { siteId: siteId.toString() }),
        ...(san && { san }),
        ...(include && { include }),
        ...(aggregate && { aggregate: 'true' }),
        utcOffset: format(new Date(), 'xxx'),
    }).toString();

    const path = `/event-analytic/energy-usage/${batchId}?${queryString}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();
    if (response.ok) {
        return respBody;
    }

    throw new Error(response.statusText);
};

export const exportEventAnalyticEnergyUsage = async (params: IEventAnalyticEnergyUsageParams, signal?: AbortSignal | null) => {
    const { batchId, companyId, siteId, include, aggregate } = params;
    const queryString = new URLSearchParams({
        format: 'csv',
        ...(companyId && { companyId: companyId.toString() }),
        ...(siteId && { siteId: siteId.toString() }),
        ...(include && { include }),
        ...(aggregate && { aggregate: 'true' }),
        utcOffset: format(new Date(), 'xxx'),
    }).toString();

    const path = `/event-analytic/energy-usage/${batchId}?${queryString}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'text/csv',
        },
    });

    if (response.ok) {
        const respBody: string = await response.text();
        const fileName = `event-analytics-energy-data--${format(new Date(), DATE_FORMAT_CSV)}.csv`;
        exportCSVData(respBody, fileName);
    } else {
        const respBody: any = await response.json();
        throw new Error(respBody?.error?.message || `Cannot export data!`);
    }
};

export const getEarliestEventInBatch = async (params: IFetchEarliestEventInBatchParams, signal?: AbortSignal | null): Promise<IEvent> => {
    const { batchId, companyId } = params;
    const queryString = new URLSearchParams({
        ...(companyId && { companyId: companyId.toString() }),
    }).toString();

    const path = `/event-analytic/earliest-event/${batchId}?${queryString}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();
    if (response.ok) {
        return respBody;
    }

    throw new Error(response.statusText);
};
